import {
  Box,
  HStack,
  Link,
  Radio,
  RadioGroup,
  Text,
  useDisclosure,
  VStack,
} from '@chakra-ui/react';
import { useContext } from 'react';
import InvoicesToPayContext from '../contexts/InvoicesToPayContext';
import SubscriptionsContext from '../contexts/SubscriptionsContext';
import useCurrencyFormatter from '../hooks/useCurrencyFormatter';
import { PALOMMA_PLUS } from '../data/subscriptions';
import BondaDetailModal from './BondaDetailModal';

const PushSubscriptionSelector = (): JSX.Element | null => {
  const {
    showSubscriptionOffer,
    selectedSubscription,
    selectSubscription,
    offer,
  } = useContext(SubscriptionsContext);
  const { invoiceSelectionLocked } = useContext(InvoicesToPayContext);
  const formatCurrency = useCurrencyFormatter();
  const { isOpen, onOpen, onClose } = useDisclosure();

  if (!showSubscriptionOffer || !offer) return null;

  const radioValue =
    selectedSubscription === true
      ? 'yes'
      : selectedSubscription === false
      ? 'no'
      : '';

  const rowStyles = (selected: boolean) => ({
    border: '1px solid',
    borderColor: selected ? 'rentals.primary.txt' : '#DFDEE2',
    borderRadius: '10px',
    bg: selected ? 'rgba(22,18,54,0.025)' : 'white',
    cursor: invoiceSelectionLocked ? 'not-allowed' : 'pointer',
    transition: 'border-color 0.2s, background-color 0.2s',
    opacity: invoiceSelectionLocked ? 0.85 : 1,
    _hover: invoiceSelectionLocked
      ? undefined
      : {
          borderColor: selected ? 'rentals.primary.txt' : 'gray.300',
          bg: selected ? 'rgba(22,18,54,0.025)' : 'gray.50',
        },
  });

  return (
    <Box width="100%" mb={6}>
      <VStack align="stretch" spacing={3}>
        <Box>
          <Text fontWeight={600}>Beneficios opcionales</Text>
          <Text fontSize="sm" color="gray.500">
            Puedes agregar Palomma Plus a este pago.
          </Text>
        </Box>

        <RadioGroup
          value={radioValue}
          onChange={(value) => selectSubscription(value === 'yes')}
          isDisabled={invoiceSelectionLocked}
        >
          <VStack align="stretch" spacing={2}>
            <Box as="label" px={4} py={3.5} {...rowStyles(selectedSubscription === true)}>
              <HStack align="flex-start" spacing={3}>
                <Radio
                  value="yes"
                  mt={1}
                  colorScheme="purple"
                  pointerEvents="none"
                  sx={{
                    _disabled: {
                      opacity: 1,
                    },
                  }}
                />

                <Box flex={1} minW={0}>
                  <HStack justify="space-between" align="flex-start" spacing={3}>
                    <Text fontWeight={600}>Sí, agregar Palomma Plus</Text>
                    <Text
                      fontWeight={600}
                      whiteSpace="nowrap"
                      color="rentals.primary.txt"
                    >
                      {formatCurrency(offer.price)}/mes
                    </Text>
                  </HStack>

                  <Text fontSize="sm" color="gray.600" mt={1.5} lineHeight="1.45">
                    Recibe {offer.puntosColombia} Puntos Colombia, participa por{' '}
                    {formatCurrency(PALOMMA_PLUS.rewardAmount)} y accede a 800+
                    descuentos.
                  </Text>

                  <HStack spacing={3} mt={2} flexWrap="wrap">
                    <Link
                      as="button"
                      color="purple.500"
                      fontSize="xs"
                      fontWeight={600}
                      textDecoration="underline"
                      onClick={onOpen}
                    >
                      Ver beneficios
                    </Link>
                    <Link
                      href={PALOMMA_PLUS.termsUrl}
                      isExternal
                      color="purple.500"
                      fontSize="xs"
                      fontWeight={600}
                      textDecoration="underline"
                    >
                      Ver términos
                    </Link>
                  </HStack>
                </Box>
              </HStack>
            </Box>

            <Box as="label" px={4} py={3.5} {...rowStyles(selectedSubscription === false)}>
              <HStack align="center" spacing={3}>
                <Radio
                  value="no"
                  colorScheme="purple"
                  pointerEvents="none"
                  sx={{
                    _disabled: {
                      opacity: 1,
                    },
                  }}
                />
                <Text fontWeight={500}>No, continuar sin beneficios</Text>
              </HStack>
            </Box>
          </VStack>
        </RadioGroup>

        {selectedSubscription === null && (
          <Text fontSize="xs" color="gray.500">
            Elige una opción para continuar.
          </Text>
        )}
      </VStack>

      <BondaDetailModal isOpen={isOpen} onClose={onClose} />
    </Box>
  );
};

export default PushSubscriptionSelector;
